import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { auditLogService, userService } from '../services/api';

const ACTION_OPTIONS = ['create', 'update', 'delete', 'share', 'upload', 'move'];
const ENTITY_TYPE_OPTIONS = ['folder', 'project', 'process'];

const ACTION_BADGE_STYLE = {
  create: { background: 'rgba(130,255,207,0.15)', color: 'rgba(130,255,207,0.9)', border: '1px solid rgba(130,255,207,0.3)' },
  update: { background: 'rgba(251,191,36,0.12)', color: 'rgba(251,191,36,0.85)', border: '1px solid rgba(251,191,36,0.25)' },
  delete: { background: 'rgba(230,100,97,0.15)', color: 'rgba(230,100,97,0.9)', border: '1px solid rgba(230,100,97,0.3)' },
  share: { background: 'rgba(168,85,247,0.15)', color: 'rgba(168,85,247,0.9)', border: '1px solid rgba(168,85,247,0.3)' },
  upload: { background: 'rgba(59,130,246,0.15)', color: 'rgba(59,130,246,0.9)', border: '1px solid rgba(59,130,246,0.3)' },
  move: { background: 'rgba(6,182,212,0.15)', color: 'rgba(6,182,212,0.9)', border: '1px solid rgba(6,182,212,0.3)' },
};

const ENTITY_BADGE_STYLE = {
  folder: { background: 'rgba(251,191,36,0.12)', color: 'rgba(251,191,36,0.85)', border: '1px solid rgba(251,191,36,0.25)' },
  project: { background: 'rgba(59,130,246,0.12)', color: 'rgba(59,130,246,0.85)', border: '1px solid rgba(59,130,246,0.25)' },
  process: { background: 'rgba(168,85,247,0.12)', color: 'rgba(168,85,247,0.85)', border: '1px solid rgba(168,85,247,0.25)' },
};

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
};

const formatDetails = (details) => {
  if (!details) return '';
  const parts = [];
  if (details.oldName && details.newName && details.oldName !== details.newName) {
    parts.push(`Renamed: ${details.oldName} -> ${details.newName}`);
  }
  if (details.parentId) parts.push(`Parent: ${details.parentId.slice(0, 8)}...`);
  if (details.folderId) parts.push(`Folder: ${details.folderId.slice(0, 8)}...`);
  if (details.projectId) parts.push(`Project: ${details.projectId.slice(0, 8)}...`);
  if (details.fileName) parts.push(`File: ${details.fileName}`);
  if (details.version) parts.push(`v${details.version}`);
  if (details.sharedWithAll !== undefined) {
    parts.push(details.sharedWithAll ? 'Shared with all' : `Shared with ${details.userIds?.length || 0} users`);
  }
  if (details.changes) parts.push(`Changed: ${details.changes.join(', ')}`);
  if (details.isPersonal) parts.push('Personal');
  if (details.hasUpload) parts.push('With upload');
  return parts.join(' | ') || JSON.stringify(details);
};

const AuditLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    userId: '',
    action: '',
    entityType: '',
    startDate: '',
    endDate: '',
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const loadUsers = useCallback(async () => {
    try {
      const response = await userService.getAllUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  const loadLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await auditLogService.getLogs({
        page,
        pageSize: pagination.pageSize,
        userId: filters.userId || undefined,
        action: filters.action || undefined,
        entityType: filters.entityType || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy,
        sortOrder,
      });
      const data = response.data || {};
      setLogs(data.items || []);
      setPagination(prev => ({
        ...prev,
        page: data.page || 1,
        total: data.total || 0,
        totalPages: data.totalPages || 0,
      }));
    } catch (error) {
      console.error('Error loading audit logs:', error);
      setMessage({ type: 'error', text: 'Failed to load audit logs' });
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, sortOrder, pagination.pageSize]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadLogs(1);
  }, [filters, sortBy, sortOrder]);

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ userId: '', action: '', entityType: '', startDate: '', endDate: '' });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handlePageChange = (newPage) => {
    loadLogs(newPage);
  };

  const handleExportCsv = async () => {
    try {
      const response = await auditLogService.getLogs({
        pageSize: 200,
        userId: filters.userId || undefined,
        action: filters.action || undefined,
        entityType: filters.entityType || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy,
        sortOrder,
      });
      const items = response.data?.items || [];

      const headers = ['Date', 'User', 'Email', 'Action', 'Entity Type', 'Entity Name', 'Details'];
      const rows = items.map(log => [
        log.createdAt ? new Date(log.createdAt).toLocaleString() : '',
        log.user?.name || 'Deleted User',
        log.user?.email || '',
        log.action,
        log.entityType,
        log.entityName || '',
        log.details ? JSON.stringify(log.details) : '',
      ]);

      const csvContent = [headers, ...rows]
        .map(row => row.map(escapeCsvValue).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      setMessage({ type: 'error', text: 'Failed to export audit logs' });
    }
  };

  const sortIndicator = (field) => {
    if (sortBy !== field) return '';
    return sortOrder === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  const startIndex = (pagination.page - 1) * pagination.pageSize + 1;
  const endIndex = Math.min(pagination.page * pagination.pageSize, pagination.total);

  const selectStyle = {
    background: 'var(--tqa-surface-2)',
    border: '1px solid var(--tqa-border-dark)',
    color: 'rgba(255,255,255,0.85)',
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: 'rgba(255,255,255,0.85)' }}>Audit Logs</h3>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Track user actions across folders, projects, and processes.
          </p>
        </div>
        <button onClick={handleExportCsv} className="btn-secondary px-4 py-2 text-sm rounded-md flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-4 p-3 rounded-lg ${message.type === 'success'
          ? 'bg-green-100 text-green-800 border border-green-200'
          : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 rounded-lg" style={{ background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
        <div className="flex flex-col">
          <label className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>User</label>
          <select
            value={filters.userId}
            onChange={(e) => handleFilterChange('userId', e.target.value)}
            className="px-2 py-1.5 rounded text-sm focus:outline-none"
            style={selectStyle}
          >
            <option value="">All Users</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name || u.email}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Action</label>
          <select
            value={filters.action}
            onChange={(e) => handleFilterChange('action', e.target.value)}
            className="px-2 py-1.5 rounded text-sm focus:outline-none"
            style={selectStyle}
          >
            <option value="">All Actions</option>
            {ACTION_OPTIONS.map(a => (
              <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Entity Type</label>
          <select
            value={filters.entityType}
            onChange={(e) => handleFilterChange('entityType', e.target.value)}
            className="px-2 py-1.5 rounded text-sm focus:outline-none"
            style={selectStyle}
          >
            <option value="">All Types</option>
            {ENTITY_TYPE_OPTIONS.map(t => (
              <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col">
          <label className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>From</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="px-2 py-1.5 rounded text-sm focus:outline-none"
            style={selectStyle}
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>To</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="px-2 py-1.5 rounded text-sm focus:outline-none"
            style={selectStyle}
          />
        </div>

        <button
          onClick={handleClearFilters}
          className="px-3 py-1.5 text-sm rounded transition-colors"
          style={{ color: 'rgba(255,255,255,0.6)', border: '1px solid var(--tqa-border-dark)' }}
        >
          Clear
        </button>
      </div>

      {/* Table */}
      <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--tqa-border-dark)' }}>
        <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm" style={{ color: 'rgba(255,255,255,0.85)' }}>
            <thead>
              <tr style={{ background: 'var(--tqa-surface)', borderBottom: '1px solid var(--tqa-border-dark)' }}>
                <th
                  className="text-left px-4 py-3 font-medium cursor-pointer select-none"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                  onClick={() => handleSort('createdAt')}
                >
                  Date{sortIndicator('createdAt')}
                </th>
                <th className="text-left px-4 py-3 font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>User</th>
                <th
                  className="text-left px-4 py-3 font-medium cursor-pointer select-none"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                  onClick={() => handleSort('action')}
                >
                  Action{sortIndicator('action')}
                </th>
                <th
                  className="text-left px-4 py-3 font-medium cursor-pointer select-none"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                  onClick={() => handleSort('entityType')}
                >
                  Entity Type{sortIndicator('entityType')}
                </th>
                <th
                  className="text-left px-4 py-3 font-medium cursor-pointer select-none"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                  onClick={() => handleSort('entityName')}
                >
                  Entity Name{sortIndicator('entityName')}
                </th>
                <th className="text-left px-4 py-3 font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{ borderColor: 'var(--tqa-green)' }}></div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    No audit logs found
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr
                    key={log.id}
                    style={{ borderBottom: '1px solid var(--tqa-border-dark)' }}
                    className="hover:bg-white/5 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {log.user?.avatarUrl && (
                          <img
                            src={log.user.avatarUrl}
                            alt=""
                            className="w-5 h-5 rounded-full"
                            referrerPolicy="no-referrer"
                          />
                        )}
                        <span className="text-sm">{log.user?.name || log.user?.email || 'Deleted User'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-0.5 text-xs font-medium rounded-full"
                        style={ACTION_BADGE_STYLE[log.action] || {}}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-0.5 text-xs font-medium rounded-full"
                        style={ENTITY_BADGE_STYLE[log.entityType] || {}}
                      >
                        {log.entityType}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[200px] truncate" title={log.entityName || ''}>
                      {log.entityName || '-'}
                    </td>
                    <td className="px-4 py-3 text-xs max-w-[250px] truncate" style={{ color: 'rgba(255,255,255,0.5)' }} title={log.details ? JSON.stringify(log.details) : ''}>
                      {formatDetails(log.details)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Showing {startIndex}-{endIndex} of {pagination.total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-30"
              style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.7)' }}
            >
              Previous
            </button>
            <span className="text-sm px-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-30"
              style={{ border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.7)' }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogsTab;
